/**
 * CycleCloseModal — Phase 4 trainer-facing close + report flow.
 *
 * TRAINER / ADMIN. English copy (operational-UI boundary).
 *
 * On open it fetches the cycle snapshot (which now carries `client_recap`)
 * so the trainer previews exactly what the client will receive, then lets
 * them add:
 *   * Internal notes  — private, never shown to the client.
 *   * Client notes    — shown to the client alongside the recap.
 *   * "Share with client" toggle — when on, closing publishes the report
 *     and the client gets an in-app banner.
 *
 * Confirm → onConfirm({ internal_notes, client_notes, share_with_client }).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FOCUS_LABELS, formatWeekN } from './useCycles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value }) => (
  <div className="bg-slate-800/60 border border-slate-700/60 rounded px-2.5 py-1.5">
    <div className="text-slate-400 text-[10px] uppercase tracking-wider">{label}</div>
    <div className="text-white text-sm font-semibold">{value}</div>
  </div>
);

export const CycleCloseModal = ({ open, cycle, onClose, onConfirm }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [share, setShare] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadSnapshot = useCallback(async (cycleId) => {
    setLoadingSnap(true);
    try {
      const r = await axios.get(`${API}/cycles/${cycleId}/snapshot`);
      setSnapshot(r.data);
    } catch (e) {
      console.error('[CycleCloseModal] snapshot failed', e);
      setSnapshot(null);
    } finally {
      setLoadingSnap(false);
    }
  }, []);

  useEffect(() => {
    if (open && cycle?.id) {
      setInternalNotes(cycle.internal_notes || '');
      setClientNotes(cycle.client_notes || '');
      setShare(true);
      setError(null);
      loadSnapshot(cycle.id);
    }
  }, [open, cycle?.id, cycle?.internal_notes, cycle?.client_notes, loadSnapshot]);

  if (!open || !cycle) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        internal_notes: internalNotes.trim() || null,
        client_notes: clientNotes.trim() || null,
        share_with_client: share,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not close cycle');
    } finally {
      setSubmitting(false);
    }
  };

  const s = snapshot;
  const prCount = s?.top_prs?.length || 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="cycle-close-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-full max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <h2 className="text-white text-lg font-semibold">Close cycle & generate report</h2>
        <p className="text-slate-400 text-sm mt-0.5 mb-4">
          {FOCUS_LABELS[cycle.focus] || cycle.focus} · {formatWeekN(cycle)} — this freezes a
          report snapshot and ends the block.
        </p>

        {/* Snapshot highlights */}
        {loadingSnap ? (
          <div className="text-slate-400 text-sm animate-pulse mb-4">Computing report…</div>
        ) : s ? (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3">
              <Stat label="Adherence" value={s.adherence_percent != null ? `${s.adherence_percent}%` : '—'} />
              <Stat label="Sessions" value={s.sessions_completed} />
              <Stat label="Weekly" value={s.weekly_frequency_actual != null ? `${s.weekly_frequency_actual}×` : '—'} />
              <Stat label="PRs" value={prCount} />
            </div>

            {/* Spanish recap preview — what the client will read */}
            <div className="mb-4">
              <div className="text-slate-400 text-[11px] uppercase tracking-wider mb-1">
                Client recap preview (Spanish)
              </div>
              <div
                className="bg-blue-950/40 border border-blue-800/40 rounded px-3 py-2.5 text-blue-100 text-sm leading-relaxed"
                data-testid="cycle-close-recap-preview"
              >
                {s.client_recap || 'No recap available.'}
              </div>
            </div>
          </>
        ) : (
          <div className="text-slate-400 text-sm mb-4">Snapshot unavailable — you can still close the cycle.</div>
        )}

        {/* Client-visible notes */}
        <label className="block text-slate-300 text-sm mb-1">
          Client-visible note <span className="text-slate-500">(Spanish, optional)</span>
        </label>
        <textarea
          value={clientNotes}
          onChange={(e) => setClientNotes(e.target.value)}
          rows={2}
          placeholder="e.g. ¡Gran ciclo! En el próximo subimos cargas en sentadilla."
          className="w-full mb-4 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm placeholder-slate-500"
          data-testid="cycle-close-client-notes"
        />

        {/* Internal notes */}
        <label className="block text-slate-300 text-sm mb-1">
          Internal note <span className="text-slate-500">(private — never shown to client)</span>
        </label>
        <textarea
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          rows={2}
          placeholder="e.g. Shoulder niggle weeks 5-6, watch overhead volume next block."
          className="w-full mb-4 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm placeholder-slate-500"
          data-testid="cycle-close-internal-notes"
        />

        {/* Share toggle */}
        <label className="flex items-center gap-2 mb-5 cursor-pointer select-none" data-testid="cycle-close-share-toggle">
          <input
            type="checkbox"
            checked={share}
            onChange={(e) => setShare(e.target.checked)}
            className="w-4 h-4 accent-emerald-500"
          />
          <span className="text-slate-200 text-sm">
            Share report with client <span className="text-slate-500">(shows an in-app banner)</span>
          </span>
        </label>

        {error && (
          <div className="text-red-300 text-sm mb-3" data-testid="cycle-close-error">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-300 hover:text-white text-sm"
            data-testid="cycle-close-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded text-sm font-medium"
            data-testid="cycle-close-confirm-btn"
          >
            {submitting ? 'Closing…' : share ? 'Close & share' : 'Close cycle'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CycleCloseModal;
